import { v4 as uuidv4 } from "uuid";
import {
  isDynamicToolUIPart,
  isReasoningUIPart,
  isTextUIPart,
  isToolUIPart,
  type DynamicToolUIPart,
  type UIMessage,
  type UIDataTypes,
  type UITools,
} from "ai";
import type { ExecutionJob, ExecutionResultMessage } from "../types/executionJobTypes";
import { isRecord } from "../../../common/utils/isRecord";
import {
  getReasoningTimeFromContentPart,
  getReasoningTimeFromProviderMetadata,
  withReasoningTimeProviderMetadata,
} from "./reasoningTime";
import {
  getToolResultProviderMetadata,
  getToolTimeFromContentPart,
  getToolTimeFromProviderMetadata,
  readToolOutput,
  toToolResultOutput,
  withToolTimeProviderMetadata,
} from "../../tool-calling";

type JobResult = NonNullable<ExecutionJob["result"]>;
type AnyUIMessagePart = UIMessage<unknown, UIDataTypes, UITools>["parts"][number];

interface PromptUIMessageMetadata {
  usage?: JobResult["usage"];
  cost?: JobResult["cost"];
  execution_time?: JobResult["execution_time"];
  finish_reason?: JobResult["finish_reason"] | null;
  jobId?: string;
}

export type PromptUIMessage = UIMessage<PromptUIMessageMetadata>;

/**
 * Convert a single ModelMessage-shaped result message into UIMessage parts.
 */
function modelContentToUIParts(content: unknown): AnyUIMessagePart[] {
  if (typeof content === "string") {
    return content ? [{ type: "text", text: content }] : [];
  }

  if (!Array.isArray(content)) {
    return [];
  }

  const parts: AnyUIMessagePart[] = [];

  for (const part of content) {
    if (!isRecord(part) || typeof part.type !== "string") continue;

    switch (part.type) {
      case "text":
        if (typeof part.text === "string") {
          parts.push({ type: "text", text: part.text });
        }
        break;
      case "reasoning":
        if (typeof part.text === "string") {
          const reasoning_time = getReasoningTimeFromContentPart(part);
          const state = part.state === "streaming" || part.state === "done" ? part.state : "done";
          // Keep empty streaming placeholders so the UI can show "Assistant is reasoning..."
          if (!part.text && state !== "streaming") break;
          parts.push({
            type: "reasoning",
            text: part.text,
            state,
            ...(reasoning_time
              ? { providerMetadata: withReasoningTimeProviderMetadata(reasoning_time) }
              : {}),
          });
        }
        break;
      case "tool-call": {
        const toolCallId = typeof part.toolCallId === "string" ? part.toolCallId : uuidv4();
        const toolName = typeof part.toolName === "string" ? part.toolName : "tool";
        parts.push({
          type: "dynamic-tool",
          toolCallId,
          toolName,
          state: "input-available",
          input: part.input ?? part.args ?? {},
        });
        break;
      }
      case "tool-result": {
        const toolCallId = typeof part.toolCallId === "string" ? part.toolCallId : uuidv4();
        const toolName = typeof part.toolName === "string" ? part.toolName : "tool";
        const { value, errorText } = readToolOutput(part.output ?? part.result);
        const resultProviderMetadata = withToolTimeProviderMetadata(
          getToolTimeFromContentPart(part),
        );
        const common = {
          type: "dynamic-tool",
          toolCallId,
          toolName,
          input: part.input ?? {},
          ...(resultProviderMetadata ? { resultProviderMetadata } : {}),
        } as const;
        parts.push(
          errorText != null
            ? { ...common, state: "output-error", errorText }
            : { ...common, state: "output-available", output: value },
        );
        break;
      }
      default:
        break;
    }
  }

  return parts;
}

/**
 * Fold each tool result into the part that holds its call.
 *
 * A tool call and its result live in two different messages (`assistant` then
 * `tool`), which would otherwise render as two tool blocks — one stuck waiting
 * for output, one without the input that produced it.
 */
function mergeToolCallsWithResults(messages: PromptUIMessage[]): PromptUIMessage[] {
  const resultsById = new Map<string, DynamicToolUIPart>();
  const callIds = new Set<string>();

  for (const message of messages) {
    for (const part of message.parts) {
      if (!isDynamicToolUIPart(part)) continue;
      if (callIds.has(part.toolCallId)) {
        resultsById.set(part.toolCallId, part);
      } else {
        callIds.add(part.toolCallId);
      }
    }
  }

  if (resultsById.size === 0) return messages;

  const merged = new Set<string>();

  return messages.map((message) => ({
    ...message,
    parts: message.parts.flatMap((part): PromptUIMessage["parts"] => {
      if (!isDynamicToolUIPart(part)) return [part];
      // The result was already folded into the call rendered above.
      if (merged.has(part.toolCallId)) return [];
      merged.add(part.toolCallId);

      const result = resultsById.get(part.toolCallId);
      if (!result) return [part];
      // The result message repeats neither the input nor a usable name.
      if (result.state === "output-error") {
        return [{ ...result, input: part.input, toolName: part.toolName }];
      }
      if (result.state === "output-available") {
        return [{ ...result, input: part.input, toolName: part.toolName }];
      }
      return [part];
    }),
  }));
}

/**
 * Project a live UIMessage into ModelMessage-shaped result.messages for the
 * cache (same field the worker persists).
 */
function uiMessageToResultMessages(message: UIMessage): ExecutionResultMessage[] {
  const content: unknown[] = [];

  for (const part of message.parts) {
    if (isReasoningUIPart(part) && (part.text || part.state === "streaming")) {
      const reasoning_time = getReasoningTimeFromProviderMetadata(part.providerMetadata);
      content.push({
        type: "reasoning",
        text: part.text ?? "",
        ...(part.state ? { state: part.state } : {}),
        ...(reasoning_time ? { reasoning_time } : {}),
      });
    } else if (isTextUIPart(part) && part.text) {
      content.push({ type: "text", text: part.text });
    } else if (isDynamicToolUIPart(part) || isToolUIPart(part)) {
      const toolName =
        "toolName" in part && typeof part.toolName === "string"
          ? part.toolName
          : part.type.startsWith("tool-")
            ? part.type.slice("tool-".length)
            : part.type;
      const toolCallId =
        "toolCallId" in part && typeof part.toolCallId === "string" ? part.toolCallId : uuidv4();
      const input = "input" in part ? part.input : {};
      if (part.state === "output-available" || part.state === "output-error") {
        // The call is kept alongside the result so a cached run reloads with the
        // arguments the model sent, matching what the worker persists.
        content.push({ type: "tool-call", toolCallId, toolName, input });
        const tool_time = getToolTimeFromProviderMetadata(
          isDynamicToolUIPart(part) ? getToolResultProviderMetadata(part) : undefined,
        );
        content.push({
          type: "tool-result",
          toolCallId,
          toolName,
          output: toToolResultOutput(
            "output" in part ? part.output : undefined,
            part.state === "output-error" ? part.errorText : undefined,
          ),
          ...(tool_time ? { tool_time } : {}),
        });
      } else {
        content.push({
          type: "tool-call",
          toolCallId,
          toolName,
          input,
        });
      }
    }
  }

  if (content.length === 0) return [];

  return [
    {
      role: "assistant",
      content,
    },
  ];
}

/**
 * Map ModelMessage `result.messages` into AI SDK UIMessage[] for rendering.
 */
function convertResultMessagesToUIMessages(
  result: JobResult | null | undefined,
  options?: { jobId?: string },
): PromptUIMessage[] {
  if (!result?.messages?.length) return [];

  const metadata: PromptUIMessageMetadata = {
    usage: result.usage,
    cost: result.cost,
    execution_time: result.execution_time,
    finish_reason: result.finish_reason ?? null,
    jobId: options?.jobId,
  };

  const messages = result.messages.map((message: ExecutionResultMessage, index) => {
    const role = message.role === "tool" ? "assistant" : message.role;
    return {
      id: `${options?.jobId ?? "job"}-${index}`,
      role: role as PromptUIMessage["role"],
      parts: modelContentToUIParts(message.content),
      ...(index === 0 ? { metadata } : {}),
    };
  });

  return mergeToolCallsWithResults(messages);
}

/**
 * Canonical UI messages for a job result — always derived from `result.messages`.
 */
function getJobUIMessages(
  result: JobResult | null | undefined,
  options?: { jobId?: string },
): PromptUIMessage[] {
  return convertResultMessagesToUIMessages(result, options);
}

export {
  convertResultMessagesToUIMessages,
  getJobUIMessages,
  modelContentToUIParts,
  uiMessageToResultMessages,
  type PromptUIMessageMetadata,
};
